import { memo, useCallback, useEffect, useState, type ComponentProps } from 'react';
import { useTranslation } from 'react-i18next';
import { Add, Cached, Close, Delete, Edit, Save, Search } from '@mui/icons-material';
import { Box, Checkbox, Chip, IconButton, InputAdornment, MenuItem, Switch, Tooltip, Typography } from '@mui/material';
import { shake } from 'radash';

import {
  AdvancedToggle,
  ChipsContainer,
  Container,
  Content,
  FilterItem,
  FilterText,
  Footer,
  FormRow,
  Header,
  LogicalOperatorChip,
  NoFiltersText,
  RangeRow,
  SavedFilterItem,
  Section,
  SectionTitle,
  StyledDrawer,
  aboveBrowseFilterDrawerSx,
  browseFilterDrawerSx,
} from './HeliosFilterDrawer.styles';

import { Button } from 'components/mui/common/Button';
import { Select } from 'components/mui/common/Select';
import { TextField } from 'components/mui/common/TextField';
import { SelectBrowseModal } from 'components/user/DummyHelios/Functions/modals/SelectBrowseModal';
import { SaveFilterModal } from './SaveFilterModal';
import { ComparisonOperator, LogicalOperator, TemplatePresentationItemDisplay } from 'types/dataHelios';

import type { FilterCondition } from 'types/tableFilters';
import type { HeliosRecordDataTablesColumn } from 'types/dataHelios';
import type { HeliosUserFilter } from 'types/heliosUserFilters';
import { getLocalizedHeliosColumnName } from 'utils/helios/language';
import { getImplicitComparisonOperator } from 'utils/filterFunctions';
import { createSelectItemLookup } from 'utils/selectItemLookup';
import {
  BLANK_OPERATORS,
  getOperatorsForDataType,
  getValueInputType,
  hasFormValues,
  isEmpty,
  isDateDataType,
  isNumericDataType,
  toStringValue,
} from 'utils/helios/filterFormUtils';
import { useFilterFormState } from 'utils/hooks/useFilterFormState';
import { getFilterRowDisplay, getFormValueDisplay, getOperatorDisplayName } from 'utils/helios/filterDisplayUtils';

const { Equals } = ComparisonOperator;
const { And, Or } = LogicalOperator;

const comparisonOperatorValues = Object.values(ComparisonOperator).filter((value): value is ComparisonOperator => typeof value === 'string');

const logicalOperatorValues = Object.values(LogicalOperator).filter((value): value is LogicalOperator => typeof value === 'string');

const toComparisonOperator = (value: unknown): ComparisonOperator => {
  if (typeof value !== 'string') {
    return Equals;
  }

  return comparisonOperatorValues.find((operator) => operator === value) || Equals;
};

const toLogicalOperator = (value: unknown): LogicalOperator => {
  if (typeof value !== 'string') {
    return And;
  }

  return logicalOperatorValues.find((operator) => operator === value) || And;
};

const upsertFilterCondition = (filters: FilterCondition[], filter: FilterCondition): FilterCondition[] => {
  const index = filters.findIndex((f) => f.columnKey === filter.columnKey);
  if (index < 0) return [...filters, filter];
  return filters.map((f, i) => (i === index ? { ...filter, id: f.id, logicalOperator: filter.logicalOperator ?? f.logicalOperator } : f));
};

const mergeEditedFilterCondition = (filters: FilterCondition[], filter: FilterCondition): FilterCondition[] => {
  if (!filters.some((f) => f.id === filter.id)) return filters;
  return filters.map((f) => (f.id === filter.id ? filter : f)).filter((f) => f.id === filter.id || f.columnKey !== filter.columnKey);
};

export interface Props {
  open: boolean;
  onClose: () => void;
  columns: HeliosRecordDataTablesColumn[];
  activeFilters: FilterCondition[];
  onApplyFilters: (filters: FilterCondition[]) => void;
  // Saved filters
  savedFilters?: HeliosUserFilter[];
  onSaveFilter?: (name: string, isDefault: boolean) => void;
  onLoadFilter?: (filter: HeliosUserFilter) => FilterCondition[] | void;
  onDeleteFilter?: (filterId: string) => void;
  activeSavedFilterId?: string | null;
  onUpdateFilter?: (name: string, isDefault: boolean, conditions?: FilterCondition[]) => void;
  onUnloadFilter?: () => void;
  isSaving?: boolean;
  testIdPrefix?: string;
  aboveModal?: boolean;
  allowDefaultFilter?: boolean;
}

type FilterFieldSelectProps = ComponentProps<typeof Select> & { aboveModal?: boolean };

const FilterFieldSelect = ({ aboveModal, ...props }: FilterFieldSelectProps) => {
  if (aboveModal) {
    return (
      <Select
        {...props}
        MenuProps={{
          disableScrollLock: true,
          sx: aboveBrowseFilterDrawerSx,
        }}
      />
    );
  }

  return <Select {...props} />;
};

export const HeliosFilterDrawer = memo(
  ({
    open,
    onClose,
    columns,
    activeFilters,
    onApplyFilters,
    savedFilters,
    onSaveFilter,
    onLoadFilter,
    onDeleteFilter,
    activeSavedFilterId,
    onUpdateFilter,
    onUnloadFilter,
    isSaving,
    testIdPrefix,
    aboveModal = false,
    allowDefaultFilter = true,
  }: Props) => {
    const { t, i18n } = useTranslation();

    const [filters, setFilters] = useState<FilterCondition[]>(activeFilters);
    const [editingFilter, setEditingFilter] = useState<FilterCondition | null>(null);
    const [browseModalOpen, setBrowseModalOpen] = useState(false);
    const [saveModalOpen, setSaveModalOpen] = useState(false);
    const {
      formValues,
      isBlank,
      fromValue,
      toValue,
      staticValues,
      currentChipText,
      advancedMode,
      setColumnKey,
      setOperator,
      setValue,
      setLogicalOperator,
      setFromValue,
      setToValue,
      setCurrentChipText,
      setAdvancedMode,
      setStaticValuesFromUnknown,
      toggleIsBlank,
      addChip,
      removeChip,
      resetForm,
      loadFilterToForm,
    } = useFilterFormState();

    useEffect(() => {
      setFilters(activeFilters);
    }, [activeFilters]);

    const selectedColumn = columns.find((col) => col.columnKey === formValues.columnKey);

    const resolveOperator = useCallback((): ComparisonOperator => {
      if (!selectedColumn) {
        return Equals;
      }

      if (advancedMode) return formValues.operator;
      return getImplicitComparisonOperator({
        id: '',
        columnKey: formValues.columnKey,
        columnName: '',
        dataType: selectedColumn.dataType,
        displayType: selectedColumn.displayType,
        operator: Equals,
        value: formValues.value,
        isBlank,
        staticValues: staticValues.length > 0 ? staticValues : undefined,
        fromValue: fromValue || undefined,
        toValue: toValue || undefined,
      });
    }, [advancedMode, formValues, selectedColumn, isBlank, staticValues, fromValue, toValue]);

    const buildFilterPayload = useCallback((): FilterCondition | null => {
      if (!selectedColumn) return null;
      return shake(
        {
          id: `filter_${Date.now()}`,
          columnKey: formValues.columnKey,
          columnName: getLocalizedHeliosColumnName(i18n, selectedColumn),
          dataType: selectedColumn.dataType,
          displayType: selectedColumn.displayType,
          operator: resolveOperator(),
          value: formValues.value,
          isBlank,
          staticValues,
          fromValue,
          toValue,
          logicalOperator: filters.length > 0 ? formValues.logicalOperator : undefined,
        },
        isEmpty
      ) as FilterCondition;
    }, [formValues, selectedColumn, isBlank, staticValues, fromValue, toValue, filters.length, resolveOperator, i18n]);

    const handleAddFilter = useCallback(() => {
      const newFilter = buildFilterPayload();
      if (!newFilter) return;

      setFilters((prev) => upsertFilterCondition(prev, newFilter));
      resetForm();
    }, [buildFilterPayload, resetForm]);

    const handleUpdateFilter = useCallback(() => {
      if (!editingFilter) return;

      const payload = buildFilterPayload();
      if (!payload) return;

      setFilters((prev) =>
        mergeEditedFilterCondition(prev, {
          ...payload,
          id: editingFilter.id,
          logicalOperator: payload.logicalOperator ?? editingFilter.logicalOperator,
        })
      );
      setEditingFilter(null);
      resetForm();
    }, [editingFilter, buildFilterPayload, resetForm]);

    const handleEditFilter = useCallback(
      (filter: FilterCondition) => {
        setEditingFilter(filter);
        loadFilterToForm(filter);
      },
      [loadFilterToForm]
    );

    const handleDeleteFilter = useCallback((filterId: string) => {
      setFilters((prev) => prev.filter((f) => f.id !== filterId));
    }, []);

    const handleApplyFilters = useCallback(() => {
      const formValid = hasFormValues(selectedColumn, advancedMode, formValues, isBlank, staticValues, fromValue, toValue);

      if (formValid) {
        const newFilter = buildFilterPayload();
        if (newFilter) {
          let updatedFilters: FilterCondition[];
          if (editingFilter) {
            updatedFilters = mergeEditedFilterCondition(filters, {
              ...newFilter,
              id: editingFilter.id,
              logicalOperator: newFilter.logicalOperator ?? editingFilter.logicalOperator,
            });
          } else {
            updatedFilters = upsertFilterCondition(filters, newFilter);
          }
          onApplyFilters(updatedFilters);
          setEditingFilter(null);
          resetForm();
          onClose();
          return;
        }
      }

      onApplyFilters(filters);
      setEditingFilter(null);
      resetForm();
      onClose();
    }, [
      filters,
      onApplyFilters,
      onClose,
      selectedColumn,
      advancedMode,
      formValues,
      isBlank,
      staticValues,
      fromValue,
      toValue,
      editingFilter,
      buildFilterPayload,
      resetForm,
    ]);

    const handleReset = useCallback(() => {
      onUnloadFilter?.();
      setFilters([]);
      setEditingFilter(null);
      resetForm();
    }, [onUnloadFilter, resetForm]);

    const handleCancel = useCallback(() => {
      setFilters(activeFilters);
      setEditingFilter(null);
      resetForm();
      onClose();
    }, [activeFilters, onClose, resetForm]);

    const handleChipKeyDown = useCallback(
      (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          addChip();
        }
      },
      [addChip]
    );

    const handleChipBlur = useCallback(() => {
      if (currentChipText.trim()) {
        addChip();
      }
    }, [currentChipText, addChip]);

    const handleSelectMultiChange = useCallback(
      (e: { target: { value: unknown } }) => {
        setStaticValuesFromUnknown(e.target.value);
      },
      [setStaticValuesFromUnknown]
    );

    const availableOperators = selectedColumn ? getOperatorsForDataType(selectedColumn.dataType, selectedColumn.displayType) : [];
    const valueInputType = selectedColumn ? getValueInputType(selectedColumn.dataType, selectedColumn.displayType) : 'text';

    const isBlankDisabled = !!(!selectedColumn || (editingFilter && editingFilter.displayType !== selectedColumn.displayType));
    const valueFieldsDisabled = !selectedColumn || isBlank !== null;

    const renderDynamicValueInput = () => {
      if (!selectedColumn) return null;

      if (advancedMode) {
        if (selectedColumn.displayType === TemplatePresentationItemDisplay.StaticSelection) {
          return (
            <FilterFieldSelect
              aboveModal={aboveModal}
              fullWidth
              label={t('helios.templates.filters.value')}
              value={formValues.value}
              onChange={(e) => setValue(toStringValue(e.target.value))}
              disabled={valueFieldsDisabled || BLANK_OPERATORS.includes(formValues.operator)}>
              {selectedColumn.settings.staticSelection.selectItems.map((selectItem) => (
                <MenuItem
                  key={selectItem.key}
                  value={selectItem.key}
                  data-testid={
                    testIdPrefix ? `${testIdPrefix}-filter-value-option-${selectItem.key}` : `helios-filter-value-option-${selectItem.key}`
                  }>
                  {selectItem.value}
                </MenuItem>
              ))}
            </FilterFieldSelect>
          );
        }

        return (
          <TextField
            fullWidth
            label={t('helios.templates.filters.value')}
            type={valueInputType}
            value={getFormValueDisplay(formValues.value, selectedColumn)}
            onChange={(e) => setValue(e.target.value)}
            onClick={selectedColumn.displayType === TemplatePresentationItemDisplay.Browse ? () => setBrowseModalOpen(true) : undefined}
            disabled={valueFieldsDisabled || BLANK_OPERATORS.includes(formValues.operator)}
            slotProps={
              selectedColumn.displayType === TemplatePresentationItemDisplay.Browse
                ? {
                    input: {
                      endAdornment: (
                        <InputAdornment position="end">
                          <Search />
                        </InputAdornment>
                      ),
                    },
                  }
                : undefined
            }
          />
        );
      }

      if (selectedColumn.displayType === TemplatePresentationItemDisplay.Browse) {
        return (
          <TextField
            fullWidth
            label={t('helios.templates.filters.value')}
            type="text"
            value={getFormValueDisplay(formValues.value, selectedColumn)}
            onClick={() => setBrowseModalOpen(true)}
            disabled={valueFieldsDisabled}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <Search />
                  </InputAdornment>
                ),
              },
            }}
          />
        );
      }

      if (selectedColumn.displayType === TemplatePresentationItemDisplay.StaticSelection) {
        const selectItems = selectedColumn.settings.staticSelection.selectItems;
        const lookupValue = createSelectItemLookup(selectItems);

        return (
          <FilterFieldSelect
            aboveModal={aboveModal}
            fullWidth
            multiple
            label={t('helios.templates.filters.value')}
            value={staticValues}
            onChange={handleSelectMultiChange}
            disabled={valueFieldsDisabled}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {(Array.isArray(selected) ? selected : []).map((key) => (
                  <Chip key={key} label={lookupValue(key)} size="small" />
                ))}
              </Box>
            )}>
            {selectItems.map((selectItem) => (
              <MenuItem
                key={selectItem.key}
                value={selectItem.key}
                data-testid={testIdPrefix ? `${testIdPrefix}-filter-value-option-${selectItem.key}` : `helios-filter-value-option-${selectItem.key}`}>
                {selectItem.value}
              </MenuItem>
            ))}
          </FilterFieldSelect>
        );
      }

      if (isNumericDataType(selectedColumn.dataType)) {
        return (
          <RangeRow>
            <TextField
              fullWidth
              type="number"
              label={t('helios.templates.filters.fromValue')}
              value={fromValue}
              onChange={(e) => setFromValue(e.target.value)}
              disabled={valueFieldsDisabled}
            />
            <TextField
              fullWidth
              type="number"
              label={t('helios.templates.filters.toValue')}
              value={toValue}
              onChange={(e) => setToValue(e.target.value)}
              disabled={valueFieldsDisabled}
            />
          </RangeRow>
        );
      }

      if (isDateDataType(selectedColumn.dataType)) {
        return (
          <RangeRow>
            <TextField
              fullWidth
              type="date"
              label={t('helios.templates.filters.fromValue')}
              value={fromValue}
              onChange={(e) => setFromValue(e.target.value)}
              disabled={valueFieldsDisabled}
              slotProps={{ inputLabel: { shrink: true } }}
            />
            <TextField
              fullWidth
              type="date"
              label={t('helios.templates.filters.toValue')}
              value={toValue}
              onChange={(e) => setToValue(e.target.value)}
              disabled={valueFieldsDisabled}
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </RangeRow>
        );
      }

      if (selectedColumn.dataType === 'Boolean') {
        return (
          <FilterFieldSelect
            aboveModal={aboveModal}
            fullWidth
            label={t('helios.templates.filters.value')}
            value={formValues.value}
            onChange={(e) => setValue(toStringValue(e.target.value))}
            disabled={valueFieldsDisabled}>
            <MenuItem value="true" data-testid={testIdPrefix ? `${testIdPrefix}-filter-value-option-true` : 'helios-filter-value-option-true'}>
              {t('common.yes')}
            </MenuItem>
            <MenuItem value="false" data-testid={testIdPrefix ? `${testIdPrefix}-filter-value-option-false` : 'helios-filter-value-option-false'}>
              {t('common.no')}
            </MenuItem>
          </FilterFieldSelect>
        );
      }

      return (
        <>
          <ChipsContainer>
            {staticValues.map((chip, index) => (
              <Chip key={index} label={chip} size="small" onDelete={() => removeChip(index)} />
            ))}
          </ChipsContainer>
          <TextField
            fullWidth
            label={t('helios.templates.filters.value')}
            value={currentChipText}
            onChange={(e) => setCurrentChipText(e.target.value)}
            onKeyDown={handleChipKeyDown}
            onBlur={handleChipBlur}
            disabled={valueFieldsDisabled}
            placeholder={t('helios.templates.filters.pressEnter')}
          />
        </>
      );
    };

    return (
      <>
        <StyledDrawer anchor="right" open={open} onClose={handleCancel} sx={aboveModal ? browseFilterDrawerSx : undefined}>
          <Container>
            <Header>
              <Typography variant="h5">{t('helios.templates.filters.title')}</Typography>
              <IconButton onClick={handleCancel} size="small">
                <Close />
              </IconButton>
            </Header>

            <Content>
              {/* Saved Filters Section */}
              {savedFilters && (
                <Section>
                  <SectionTitle variant="subtitle1">{t('helios.templates.filters.savedFilters')}</SectionTitle>
                  {savedFilters.length === 0 && <NoFiltersText>{t('helios.templates.filters.noSavedFilters')}</NoFiltersText>}
                  {savedFilters.map((savedFilter) => (
                    <SavedFilterItem
                      key={savedFilter.id}
                      isActive={savedFilter.id === activeSavedFilterId}
                      onClick={() => {
                        if (savedFilter.id === activeSavedFilterId) {
                          onUnloadFilter?.();
                          setFilters([]);
                        } else {
                          const loadedFilters = onLoadFilter?.(savedFilter);
                          if (loadedFilters) {
                            setFilters(loadedFilters);
                          }
                        }
                      }}>
                      <FilterText>
                        {savedFilter.name}
                        {savedFilter.isDefault && <Chip size="small" label={t('helios.templates.filters.defaultFilter')} sx={{ ml: 1 }} />}
                      </FilterText>
                      <Tooltip title={t('helios.templates.filters.deleteFilter')}>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            onDeleteFilter?.(savedFilter.id);
                          }}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </SavedFilterItem>
                  ))}
                </Section>
              )}

              {/* Active Filters Section */}
              <Section>
                <SectionTitle variant="subtitle1">{t('helios.templates.filters.activeFilters')}</SectionTitle>
                {filters.length > 0 ? (
                  filters.map((filter, index) => (
                    <FilterItem key={filter.id}>
                      {index > 0 && <LogicalOperatorChip>{filter.logicalOperator === Or ? 'OR' : 'AND'}</LogicalOperatorChip>}
                      <FilterText>{getFilterRowDisplay(filter, columns, t)}</FilterText>
                      <IconButton size="small" onClick={() => handleEditFilter(filter)}>
                        <Edit fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDeleteFilter(filter.id)}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </FilterItem>
                  ))
                ) : (
                  <NoFiltersText>{t('helios.templates.filters.noActiveFilters')}</NoFiltersText>
                )}
              </Section>

              {/* Add/Edit Filter Section */}
              <Section>
                <SectionTitle variant="subtitle1">
                  {editingFilter ? t('helios.templates.filters.editFilter') : t('helios.templates.filters.addFilter')}
                </SectionTitle>

                <FormRow>
                  <Box display="flex" gap={1} alignItems="center">
                    <FilterFieldSelect
                      aboveModal={aboveModal}
                      fullWidth
                      label={t('helios.templates.filters.column')}
                      value={formValues.columnKey}
                      data-testid={testIdPrefix ? `${testIdPrefix}-filter-column-select` : 'helios-filter-column-select'}
                      onChange={(e) => {
                        setColumnKey(toStringValue(e.target.value));
                      }}>
                      {columns.map((column) => (
                        <MenuItem
                          key={column.columnKey}
                          value={column.columnKey}
                          data-testid={
                            testIdPrefix
                              ? `${testIdPrefix}-filter-column-option-${column.columnKey}`
                              : `helios-filter-column-option-${column.columnKey}`
                          }>
                          {getLocalizedHeliosColumnName(i18n, column)}
                        </MenuItem>
                      ))}
                    </FilterFieldSelect>

                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 45 }}>
                      <Checkbox
                        checked={isBlank === true}
                        onChange={toggleIsBlank}
                        disabled={isBlankDisabled}
                        sx={{ p: 0.5 }}
                        indeterminate={isBlank === null ? undefined : isBlank === false}
                      />
                      <Typography variant="caption" sx={{ fontSize: '0.6rem', color: 'text.secondary', textAlign: 'center', lineHeight: 1 }}>
                        {t('helios.templates.filters.isBlank')}
                      </Typography>
                    </Box>
                  </Box>

                  <AdvancedToggle
                    control={
                      <Switch
                        checked={advancedMode}
                        onChange={(e) => setAdvancedMode(e.target.checked)}
                        size="small"
                        data-testid={testIdPrefix ? `${testIdPrefix}-filter-advanced-switch` : 'helios-filter-advanced-switch'}
                      />
                    }
                    label={t('helios.templates.filters.advancedMode')}
                  />

                  {advancedMode && (
                    <>
                      <FilterFieldSelect
                        aboveModal={aboveModal}
                        fullWidth
                        label={t('helios.templates.filters.operator')}
                        value={formValues.operator}
                        data-testid={testIdPrefix ? `${testIdPrefix}-filter-operator-select` : 'helios-filter-operator-select'}
                        onChange={(e) => setOperator(toComparisonOperator(e.target.value))}
                        disabled={!selectedColumn}>
                        {availableOperators.map((operator) => (
                          <MenuItem
                            key={operator}
                            value={operator}
                            data-testid={
                              testIdPrefix ? `${testIdPrefix}-filter-operator-option-${operator}` : `helios-filter-operator-option-${operator}`
                            }>
                            {getOperatorDisplayName(operator, t)}
                          </MenuItem>
                        ))}
                      </FilterFieldSelect>

                      {filters.length > 0 && !editingFilter && (
                        <FilterFieldSelect
                          aboveModal={aboveModal}
                          fullWidth
                          label={t('helios.templates.filters.logicalOperator')}
                          value={formValues.logicalOperator}
                          data-testid={testIdPrefix ? `${testIdPrefix}-filter-logical-operator-select` : 'helios-filter-logical-operator-select'}
                          onChange={(e) => setLogicalOperator(toLogicalOperator(e.target.value))}>
                          <MenuItem
                            value={And}
                            data-testid={
                              testIdPrefix ? `${testIdPrefix}-filter-logical-operator-option-and` : 'helios-filter-logical-operator-option-and'
                            }>
                            AND
                          </MenuItem>
                          <MenuItem
                            value={Or}
                            data-testid={
                              testIdPrefix ? `${testIdPrefix}-filter-logical-operator-option-or` : 'helios-filter-logical-operator-option-or'
                            }>
                            OR
                          </MenuItem>
                        </FilterFieldSelect>
                      )}
                    </>
                  )}

                  {renderDynamicValueInput()}

                  <Box display="flex" gap={1}>
                    <Button
                      startIcon={<Add />}
                      variant="contained"
                      onClick={editingFilter ? handleUpdateFilter : handleAddFilter}
                      data-testid={testIdPrefix ? `${testIdPrefix}-add-filter-button` : 'helios-add-filter-button'}
                      disabled={!hasFormValues(selectedColumn, advancedMode, formValues, isBlank, staticValues, fromValue, toValue)}>
                      {editingFilter ? t('common.update') : t('common.add')}
                    </Button>

                    {editingFilter && (
                      <Button
                        variant="outlined"
                        onClick={() => {
                          setEditingFilter(null);
                          resetForm();
                        }}>
                        {t('common.cancel')}
                      </Button>
                    )}
                  </Box>
                </FormRow>
              </Section>
            </Content>

            <Footer>
              <Tooltip title={t('filters.reset')}>
                <IconButton
                  onClick={handleReset}
                  size="medium"
                  data-testid={testIdPrefix ? `${testIdPrefix}-reset-filters-button` : 'helios-reset-filters-button'}>
                  <Cached />
                </IconButton>
              </Tooltip>
              {filters.length > 0 && onSaveFilter && (
                <>
                  <Tooltip title={activeSavedFilterId ? t('helios.templates.filters.updateFilter') : t('helios.templates.filters.saveFilter')}>
                    <IconButton
                      onClick={() => setSaveModalOpen(true)}
                      size="medium"
                      color={activeSavedFilterId ? 'primary' : 'default'}
                      data-testid={testIdPrefix ? `${testIdPrefix}-save-filter-button` : 'helios-save-filter-button'}>
                      <Save />
                    </IconButton>
                  </Tooltip>
                </>
              )}
              <Button
                variant="contained"
                onClick={handleApplyFilters}
                sx={{ flex: 1 }}
                data-testid={testIdPrefix ? `${testIdPrefix}-apply-filters-button` : 'helios-apply-filters-button'}>
                {t('helios.templates.filters.apply')}
              </Button>
            </Footer>
          </Container>
        </StyledDrawer>

        {browseModalOpen && selectedColumn?.displayType === TemplatePresentationItemDisplay.Browse && (
          <SelectBrowseModal
            open={browseModalOpen}
            onClose={() => setBrowseModalOpen(false)}
            onSubmit={(selectedItem) => {
              setValue(JSON.stringify({ id: selectedItem.id, name: selectedItem.name }));
              setBrowseModalOpen(false);
            }}
            templateId={selectedColumn.settings.browse.codeListTemplateId}
            title={t('helios.templates.filters.selectValue')}
            sx={aboveModal ? aboveBrowseFilterDrawerSx : undefined}
          />
        )}

        <SaveFilterModal
          sx={aboveModal ? aboveBrowseFilterDrawerSx : undefined}
          open={saveModalOpen}
          onClose={() => setSaveModalOpen(false)}
          initialName={activeSavedFilterId ? savedFilters?.find((f) => f.id === activeSavedFilterId)?.name : ''}
          initialIsDefault={activeSavedFilterId ? savedFilters?.find((f) => f.id === activeSavedFilterId)?.isDefault : false}
          allowDefault={allowDefaultFilter}
          onSave={(name, isDefault) => {
            if (activeSavedFilterId && onUpdateFilter) {
              onUpdateFilter(name, isDefault, filters);
            } else {
              onSaveFilter?.(name, isDefault);
            }
            setSaveModalOpen(false);
          }}
          isLoading={isSaving}
        />
      </>
    );
  }
);
